import {PrismicRichText, SliceZone} from '@prismicio/react'
import Link from 'next/link'
import {
  AllDocumentTypes,
  NavigationDocumentData,
  PageDocumentData,
  ArticleDocumentData,
  CustomerStoryDocumentData,
  HomePageDocumentData,
} from 'prismicio-types'
import {memo, useCallback, useState} from 'react'
import {components} from 'slices'
import {Icon} from 'src/common/Icon'
import {Context} from 'src/common/types'
import {LocalesType, LOCATION_TRANSLATIONS} from 'src/common/constants'
import {useRouter} from 'next/router'
import {langs} from 'src/common/Layout/Footer/constants'
import {BadgeCell} from './BadgeCell'
import Image from 'next/image'
import {useLinkResolver} from 'prismicio'
import {EVENTS, track, getLinkLocale} from 'src/common/helpers'
import {isFilled} from '@prismicio/client'
import {CookieConsentPopup} from 'src/common/Layout/Footer/CookieConsentPopup'
import cn from 'classnames'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {track as trackMossAnalytics} from '@getmoss/moss-analytics'

type FooterV2Props = {
  alternateLanguages: Array<AllDocumentTypes['alternate_languages'][number] & {parentUid?: string}>
  breadcrumbs?:
    | PageDocumentData['breadcrumbs']
    | ArticleDocumentData['breadcrumbs']
    | CustomerStoryDocumentData['breadcrumbs']
    | HomePageDocumentData['breadcrumbs']
  className?: string
  navigationData: NavigationDocumentData
  pageUid?: string
}

export const FooterV2 = memo(function FooterV2(props: FooterV2Props) {
  const router = useRouter()
  const locale = router.locale as LocalesType
  const [isPopupShown, setIsPopupShown] = useState(false)
  const close = useCallback(() => setIsPopupShown(false), [])
  const {linkResolver} = useLinkResolver()
  const domainCountry = useGetDomainCountry()

  const showCookieConsent = useCallback(() => {
    trackMossAnalytics({
      event: 'interaction',
      interaction: {
        element: 'show-cookie-consent-pop-up',
        elementText: props.navigationData.cookie_consent_label || '',
        location: 'footer',
        type: 'click',
      },
      page: {
        type: props.pageUid || '',
        domainCountry,
      },
    })

    if (typeof window !== 'undefined') {
      // @ts-ignore -- usercentrics script is loaded by GTM
      window.UC_UI?.showSecondLayer()
    }
  }, [domainCountry, props.navigationData.cookie_consent_label, props.pageUid])

  return (
    <footer className="w-full bg-green-dark px-5 py-10 md:px-10 md:pb-12 md:pt-20 lg:px-5 xl:px-0" data-nav-dark>
      <div className="mx-auto flex h-full w-full max-w-[1200px] flex-col gap-y-12 lg:gap-y-20">
        <div className="grid h-full w-full grid-cols-1 justify-between max-lg:gap-y-12 lg:grid-cols-12 lg:grid-rows-[1fr,auto] lg:gap-x-6">
          <div className="col-span-1 row-start-1 lg:col-span-3 lg:col-start-1 lg:row-start-1">
            <Icon name="moss-woodmark" className="h-9 w-[160px] text-moss-white md:h-[43px] md:w-[192px]" />

            {/* Grid */}
          </div>
          <div className="col-span-1 row-start-3 lg:col-span-3 lg:col-start-1 lg:row-start-2">
            <div className="grid grid-cols-[repeat(2,min(50%,220px))] grid-rows-[repeat(2,118px)] text-moss-grey-100 lg:grid-cols-[repeat(2,124px)] lg:grid-rows-[repeat(2,100px)]">
              {props.navigationData.badges_section.map((cell, index) => (
                <BadgeCell key={index} badgeCell={cell.badge_cell} index={index} />
              ))}
            </div>
          </div>

          <div className="col-span-1 row-start-2 flex w-full flex-col max-lg:divide-y-[1px] max-lg:divide-moss-white/[20%] max-lg:border-b-[1px] max-lg:border-b-moss-white/[20%] lg:col-span-9 lg:col-start-4 lg:row-span-2 lg:flex-row lg:justify-end lg:gap-10 xl:gap-20">
            <SliceZone
              context={{isFooter: true, pageUid: props.pageUid, isFooterV2: true} as Context}
              components={components}
              slices={props.navigationData.slices1}
            />
            <LanguageSwitcher alternateLanguages={props.alternateLanguages} locale={locale} />
          </div>
        </div>

        <div className="flex w-full flex-col justify-between max-lg:gap-12 lg:flex-row">
          <div className="flex flex-col gap-6 text-moss-white/[70%] max-lg:order-2">
            <div className="flex gap-6 max-lg:flex-wrap max-lg:text-nowrap">
              {props.navigationData.underline_links.map((link, index) => {
                const linkLocale = getLinkLocale(link.link, router.locale as LocalesType)

                return (
                  <Link
                    key={'footerLink-' + index}
                    className="text-button-xs transition-colors hover:text-moss-white"
                    href={linkResolver(link.link)}
                    locale={linkLocale}
                  >
                    {link.label}
                  </Link>
                )
              })}
              <button className="text-button-xs" onClick={showCookieConsent} type="button">
                {props.navigationData.cookie_consent_label}
              </button>
            </div>
            <div className="text-tg-body-xs flex max-w-[385px] flex-col gap-12 lg:gap-6">
              <PrismicRichText field={props.navigationData.description} />
              <div className="flex gap-6 text-green-neon">
                <span>{props.navigationData.copyright}</span>
                <span>{props.navigationData.additional_text}</span>
              </div>
            </div>
          </div>
          {isFilled.link(props.navigationData.follow_us_link) && (
            <Link
              href={linkResolver(props.navigationData.follow_us_link)}
              className="group relative flex h-12 w-fit items-center gap-3 self-start rounded-[4px] bg-moss-white/[10%] pl-3 pr-4 backdrop-blur-[6px] lg:self-end"
              onClick={() => {
                track({
                  event: EVENTS.CLICK,
                  mktgId: 'follow-us-button',
                  pageName: props.pageUid,
                  location: `component-footer`,
                  dataType: 'follow-us-button',
                })

                trackMossAnalytics({
                  event: 'interaction',
                  interaction: {
                    element: 'follow-us-button',
                    elementText: props.navigationData.follow_us_button_label || '',
                    location: 'footer',
                    type: 'click',
                  },
                  page: {
                    type: props.pageUid || '',
                    domainCountry,
                  },
                })
              }}
            >
              <Icon name="linkedin-logo" className="h-6 w-6 text-moss-white" />
              <span className="text-button flex items-center gap-2 uppercase text-moss-white">
                {props.navigationData.follow_us_button_label}
                <Icon name="arrow" className="h-[14px] w-[9px]" />
              </span>
              <div className="pointer-events-none absolute inset-0 rounded-[4px] border-[.6px] border-moss-white/[10%]" />
            </Link>
          )}
        </div>
      </div>
      <CookieConsentPopup isShown={isPopupShown} navigationData={props.navigationData} close={close} />
    </footer>
  )
})

const LanguageSwitcher = ({
  locale,
  alternateLanguages,
}: {
  locale: LocalesType
  alternateLanguages: Array<AllDocumentTypes['alternate_languages'][number] & {parentUid?: string}>
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggleOpen = () => setIsOpen((prev) => !prev)

  return (
    <div className="flex w-min flex-col gap-y-3 max-lg:-order-1 max-lg:w-full max-lg:!border-b-[1px] max-lg:!border-t-0 max-lg:border-moss-white/[20%] max-lg:pb-6 lg:gap-y-8">
      <span className="text-button uppercase text-green-neon">{LOCATION_TRANSLATIONS[locale]}</span>
      <div className="group flex w-full flex-col">
        <button
          className="flex items-center justify-between gap-x-2 text-[14px] font-medium leading-[normal] tracking-[.14px] text-moss-white transition-colors lg:gap-4"
          type="button"
          onClick={toggleOpen}
        >
          <div className="flex items-center gap-x-2">
            <Image
              alt={`${langs[locale || 'en-us'][locale].name} flag`}
              className="h-4 w-5"
              src={langs[locale || 'en-us'][locale].icon}
            />
            {langs[locale || 'en-us'][locale].name}
          </div>
          <Icon
            name="chevron-v2"
            className={cn('h-1.5 w-2.5 text-green-neon transition-transform duration-300 ease-linear lg:hidden', {
              'rotate-180': !isOpen,
            })}
          />
        </button>

        <nav
          className={cn(
            'pointer-events-none grid gap-y-3 text-[14px] font-medium leading-[normal] tracking-[.14px] text-moss-white transition-all',
            {
              'max-h-0 opacity-0': !isOpen,
              'max-h-[1000px] opacity-100 pointer-events-auto pt-4': isOpen,
            },
          )}
        >
          {(alternateLanguages || []).map((altLang) => {
            return (
              <Link
                className="flex items-center gap-x-2"
                key={altLang.id}
                href={altLang.parentUid ? `/${altLang.parentUid}/${altLang.uid}` : `/${altLang.uid}`}
                locale={altLang.lang}
              >
                <Image
                  alt={`${langs[altLang.lang as LocalesType][altLang.lang as LocalesType].name} flag`}
                  className="h-4 w-5"
                  src={langs[altLang.lang as LocalesType][altLang.lang as LocalesType].icon}
                />
                {langs[altLang.lang as LocalesType][locale].name}
              </Link>
            )
          })}
        </nav>
      </div>
    </div>
  )
}
